import { useCallback, useEffect, useRef, useState } from "react";
import type { CapturedPhoto } from "@/types/types.d";

export function useCamera() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async () => {
    try {
      setError(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("getUserMedia is not available in this browser");
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play();
        setReady(true);
      }
    } catch (error) {
      setError((error as Error).message);
      setReady(false);
    }
  }, []);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
    setReady(false);
  }, []);

  const takePhoto = useCallback(async (): Promise<CapturedPhoto | null> => {
    const video = videoRef.current;
    if (!video || !ready) return null;

    const videoWidht = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (!videoWidht || !videoHeight) return null;

    const baseCanvas = document.createElement("canvas");
    baseCanvas.width = videoWidht;
    baseCanvas.height = videoHeight;
    const baseCtx = baseCanvas.getContext("2d");
    if (!baseCtx) return null;
    baseCtx.drawImage(video, 0, 0, videoWidht, videoHeight);

    const MAX_SIDE = 1600;
    let targetW = videoWidht;
    let targetH = videoHeight;
    let canvas = baseCanvas;

    if (Math.max(videoWidht, videoHeight) > MAX_SIDE) {
      const ratio = MAX_SIDE / Math.max(videoWidht, videoHeight);
      targetW = Math.round(videoWidht * ratio);
      targetH = Math.round(videoHeight * ratio);
      canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(baseCanvas, 0, 0, targetW, targetH);
    }

    // JPEG con calidad 0.85 (ajustable)
    const blob: Blob = await new Promise((resolve, reject) =>
      canvas.toBlob(
        (b) =>
          b ? resolve(b) : reject(new Error("Blog creation didn't work")),
        "image/jpeg",
        0.85,
      ),
    );

    const id = crypto.randomUUID();
    const file = new File([blob], `photo-${id}.jpg`, {
      type: blob.type,
      lastModified: Date.now(),
    });
    const url = URL.createObjectURL(blob);

    return {
      id,
      blob,
      file,
      url,
      width: targetW,
      height: targetH,
      createdAt: Date.now(),
    };
  }, [ready]);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return { videoRef, start, stop, takePhoto, ready, error };
}
